import React, { useState, useEffect, useCallback } from 'react'
import { Plus, Pencil, Trash2, Search, MoreHorizontal, ArrowUpRight } from 'lucide-react'
import {
  Button, Input, Label, Card, CardContent, Badge,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
} from '../../ui'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '../../ui/dropdown-menu'
import { getConceptos, createConcepto, updateConcepto, deleteConcepto, conceptoEnUso, getPrendas } from '../../../lib/supabase'
import { useNavigate } from 'react-router-dom'
import { useConfirmDialog } from '../../../hooks/useConfirmDialog'
import { useUnsavedGuard } from '../../../hooks/useUnsavedGuard'

const TIPO_OPTIONS = [
  { value: 'inscripcion', label: 'Inscripcion' },
  { value: 'mensualidad', label: 'Mensualidad' },
  { value: 'uniforme', label: 'Uniforme' },
  { value: 'evento', label: 'Evento' },
  { value: 'otro', label: 'Otro' },
]

const TIPO_COLORS = {
  inscripcion: 'bg-blue-100 text-blue-800',
  mensualidad: 'bg-green-100 text-green-800',
  uniforme: 'bg-purple-100 text-purple-800',
  evento: 'bg-amber-100 text-amber-800',
  otro: 'bg-gray-100 text-gray-800',
}

const initialForm = { nombre: '', tipo: 'otro', monto_default: '', descripcion: '' }

const ConceptosTab = () => {
  const navigate = useNavigate()
  const { confirm, alert, ConfirmDialog } = useConfirmDialog()
  const [search, setSearch] = useState('')
  const guard = useUnsavedGuard({ onClose: () => setFormOpen(false) })
  const [conceptos, setConceptos] = useState([])
  // Se guarda la prenda DUENA, no solo su id: el nombre es lo que lleva al catalogo
  // de Uniformes con esa prenda ya buscada.
  const [prendaPorConcepto, setPrendaPorConcepto] = useState({})
  const [loading, setLoading] = useState(true)
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState(initialForm)
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    const [conceptosRes, prendasRes] = await Promise.all([getConceptos(), getPrendas()])
    setConceptos((conceptosRes.data || []).filter((c) => c.activo !== false))
    setPrendaPorConcepto(Object.fromEntries(
      (prendasRes.data || []).filter((p) => p.concepto_id).map((p) => [p.concepto_id, p]),
    ))
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  const openCreate = () => { setEditing(null); setForm(initialForm); setFormOpen(true) }
  const openEdit = (c) => {
    setEditing(c)
    setForm({ nombre: c.nombre, tipo: c.tipo, monto_default: c.monto_default || '', descripcion: c.descripcion || '' })
    setFormOpen(true)
  }

  const handleSave = async (e) => {
    e.preventDefault()
    if (!form.nombre.trim()) return
    setSaving(true)
    const { error } = editing
      ? await updateConcepto(editing.id, { ...form, monto_default: form.monto_default ? Number(form.monto_default) : null })
      : await createConcepto({ ...form, monto_default: form.monto_default ? Number(form.monto_default) : null })
    setSaving(false)
    if (error) { await alert('Error', error.message || 'No se pudo guardar el concepto'); return }
    guard.resetDirty()
    setFormOpen(false)
    load()
  }

  const handleDelete = async (c) => {
    const { enUso, planes, cargos } = await conceptoEnUso(c.id)
    if (enUso) {
      await alert('No se puede eliminar', `"${c.nombre}" se usa en ${planes} plan(es)${cargos ? ` y tiene ${cargos} cargo(s)` : ''}. Quítalo de los planes o cancela los cargos primero.`)
      return
    }
    if (!await confirm('Eliminar concepto', `Eliminar concepto "${c.nombre}"?`, { destructive: true })) return
    const { error } = await deleteConcepto(c.id)
    if (error) { await alert('Error', error.message || 'No se pudo eliminar'); return }
    load()
  }

  const formatMonto = (m) => m != null ? `$${Number(m).toLocaleString('es-MX', { minimumFractionDigits: 2 })}` : '—'

  const term = search.trim().toLowerCase()
  const conceptosFiltrados = term
    ? conceptos.filter((c) =>
        (c.nombre || '').toLowerCase().includes(term) ||
        (c.tipo || '').toLowerCase().includes(term)
      )
    : conceptos

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-nowrap">
        <div className="flex-1 min-w-0 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar concepto..."
            className="pl-10"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground hidden sm:block whitespace-nowrap">
            <span className="font-medium text-foreground">{conceptosFiltrados.length}</span>{' '}
            {conceptosFiltrados.length === 1 ? 'concepto' : 'conceptos'}
          </span>
          <Button onClick={openCreate} className="h-10" aria-label="Nuevo concepto" title="Nuevo concepto">
            <Plus className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Nuevo concepto</span>
          </Button>
        </div>
      </div>

      {loading ? (
        <p className="text-muted-foreground text-sm py-8 text-center">Cargando...</p>
      ) : conceptosFiltrados.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            {conceptos.length === 0
              ? 'No hay conceptos de cobro. Crea el primero para empezar.'
              : `No hay conceptos que coincidan con "${search.trim()}".`}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead className="hidden sm:table-cell">Tipo</TableHead>
                <TableHead className="text-right">Monto</TableHead>
                <TableHead className="w-20"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {conceptosFiltrados.map((c) => {
                const prenda = prendaPorConcepto[c.id] || null
                return (
                  <TableRow key={c.id}>
                    <TableCell className="font-medium">
                      {c.nombre}
                      {/* En movil la columna Tipo se oculta, asi que el dato baja aqui */}
                      <span className="mt-1 flex flex-wrap items-center gap-1.5 sm:hidden">
                        <Badge className={`${TIPO_COLORS[c.tipo] || TIPO_COLORS.otro} text-[10px] capitalize`}>
                          {c.tipo}
                        </Badge>
                      </span>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <Badge className={`${TIPO_COLORS[c.tipo] || TIPO_COLORS.otro} text-xs capitalize`}>
                          {c.tipo}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums whitespace-nowrap">{formatMonto(c.monto_default)}</TableCell>
                    <TableCell>
                      {/* Sin badge de "Uniformes": decia casi lo mismo que el tipo
                          ("Uniforme") y repetia lo que este texto ya dice. Tres senales
                          para una idea; el dueno pregunto que significaba.
                          Y decirle "esto se gestiona alla" sin llevarlo alla era dejarlo
                          a medio camino: ahora es un enlace que abre el catalogo con esa
                          prenda ya buscada. */}
                      {prenda ? (
                        <div className="flex justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs text-muted-foreground"
                            onClick={() => navigate(`/admin/uniformes?buscar=${encodeURIComponent(prenda.nombre || '')}`)}
                            title={`Abrir "${prenda.nombre}" en Uniformes`}
                          >
                            Gestionado en Uniformes
                            <ArrowUpRight className="ml-1 h-3.5 w-3.5" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex gap-1 justify-end">
                          <Button variant="outline" size="sm" className="h-7 px-2" onClick={() => openEdit(c)}>
                            <Pencil className="h-3.5 w-3.5 sm:mr-1.5" />
                            <span className="hidden sm:inline text-xs">Editar</span>
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Mas acciones">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => handleDelete(c)}
                                className="text-destructive focus:text-destructive"
                              >
                                <Trash2 className="h-4 w-4" />
                                Eliminar concepto
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={formOpen} onOpenChange={guard.onOpenChange}>
        <DialogContent {...guard.dirtyProps} className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar Concepto' : 'Nuevo Concepto'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="space-y-2">
              <Label>Nombre *</Label>
              <Input value={form.nombre} onChange={(e) => setForm((p) => ({ ...p, nombre: e.target.value }))} placeholder="Ej: Inscripción 2026" required />
            </div>
            <div className="space-y-2">
              <Label>Tipo</Label>
              <Select value={form.tipo} onValueChange={(v) => { guard.markDirty(); setForm((p) => ({ ...p, tipo: v })) }}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TIPO_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Monto predeterminado</Label>
              <Input type="number" min="0" step="0.01" value={form.monto_default} onChange={(e) => setForm((p) => ({ ...p, monto_default: e.target.value }))} placeholder="$0.00" />
            </div>
            <div className="space-y-2">
              <Label>Descripción</Label>
              <Input value={form.descripcion} onChange={(e) => setForm((p) => ({ ...p, descripcion: e.target.value }))} placeholder="Opcional" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => guard.onOpenChange(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</Button>
            </DialogFooter>
          </form>
          {guard.DiscardDialog}
        </DialogContent>
      </Dialog>
      <ConfirmDialog />
    </div>
  )
}

export default ConceptosTab
